import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import 'xterm/css/xterm.css';

interface PtyTerminalProps {
  serverId: number;
  ws: WebSocket | null;
  wsConnected: boolean;
  hostname: string;
  fullscreen: boolean;
  onFullscreenToggle: () => void;
  /** Parent calls this to register a message handler for pty_* messages. */
  onRegisterHandler: (fn: (msg: any) => void) => void;
}

const PtyTerminal: React.FC<PtyTerminalProps> = ({
  ws,
  wsConnected,
  hostname,
  fullscreen,
  onFullscreenToggle,
  onRegisterHandler,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const pendingCreateRef = useRef<string | null>(null);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  // Initialise terminal once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: '"Cascadia Code", "Fira Code", "JetBrains Mono", monospace',
      theme: {
        background: '#0d0d14',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        selectionBackground: '#264f78',
        black: '#000000',
        brightBlack: '#666666',
        red: '#cd3131',
        brightRed: '#f14c4c',
        green: '#0dbc79',
        brightGreen: '#23d18b',
        yellow: '#e5e510',
        brightYellow: '#f5f543',
        blue: '#2472c8',
        brightBlue: '#3b8eea',
        magenta: '#bc3fbc',
        brightMagenta: '#d670d6',
        cyan: '#11a8cd',
        brightCyan: '#29b8db',
        white: '#e5e5e5',
        brightWhite: '#e5e5e5',
      },
      scrollback: 2000,
      allowProposedApi: true,
    });

    const fit = new FitAddon();
    const links = new WebLinksAddon();
    term.loadAddon(fit);
    term.loadAddon(links);
    term.open(containerRef.current);

    // Small delay so the DOM is fully laid out before fitting
    setTimeout(() => fit.fit(), 50);

    termRef.current = term;
    fitRef.current = fit;

    // Send keystrokes → agent PTY
    term.onData((data) => {
      if (!ws || !sessionIdRef.current) return;
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({
        type: 'pty_input',
        payload: { sessionId: sessionIdRef.current, data },
      }));
    });

    // ResizeObserver — refit + notify agent on container resize
    const ro = new ResizeObserver(() => {
      if (!fitRef.current || !ws || !sessionIdRef.current) return;
      if (ws.readyState !== WebSocket.OPEN) return;
      try {
        fitRef.current.fit();
        ws.send(JSON.stringify({
          type: 'pty_resize',
          payload: {
            sessionId: sessionIdRef.current,
            rows: term.rows,
            cols: term.cols,
          },
        }));
      } catch { /* ignore */ }
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      term.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fit when fullscreen toggles
  useEffect(() => {
    setTimeout(() => fitRef.current?.fit(), 80);
  }, [fullscreen]);

  // Register WS message handler with parent (once)
  useEffect(() => {
    onRegisterHandler((msg: any) => {
      const { type, payload, requestId } = msg;

      if (type === 'pty_create_result') {
        if (requestId && requestId === pendingCreateRef.current) {
          pendingCreateRef.current = null;
          if (payload?.error) {
            termRef.current?.write(`\r\nError: ${payload.error}\r\n`);
            setConnecting(false);
          } else if (payload?.sessionId) {
            sessionIdRef.current = payload.sessionId;
            setSessionId(payload.sessionId);
            setConnecting(false);
            // Focus terminal after connect
            setTimeout(() => termRef.current?.focus(), 100);
          }
        }
        return;
      }

      if (type === 'pty_output') {
        const { sessionId: sid, data } = payload || {};
        if (sid === sessionIdRef.current && data) {
          termRef.current?.write(data);
        }
        return;
      }

      if (type === 'pty_closed') {
        const { sessionId: sid, exitCode } = payload || {};
        if (sid === sessionIdRef.current) {
          termRef.current?.write(`\r\n\r\n[Process exited with code ${exitCode ?? 0}]\r\n`);
          sessionIdRef.current = null;
          setSessionId(null);
        }
        return;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !termRef.current) return;
    setConnecting(true);
    termRef.current.clear();
    termRef.current.write('\r\nConnecting...\r\n');

    fitRef.current?.fit();
    const cols = termRef.current.cols || 80;
    const rows = termRef.current.rows || 24;

    const requestId = Math.random().toString(36).slice(2);
    pendingCreateRef.current = requestId;

    ws.send(JSON.stringify({
      type: 'pty_create',
      requestId,
      payload: { cols, rows },
    }));

    // Timeout guard
    setTimeout(() => {
      if (pendingCreateRef.current === requestId) {
        pendingCreateRef.current = null;
        setConnecting(false);
        termRef.current?.write('\r\nConnection timed out. Is the agent online?\r\n');
      }
    }, 12000);
  };

  const handleDisconnect = () => {
    if (!ws || !sessionIdRef.current) return;
    ws.send(JSON.stringify({
      type: 'pty_close',
      payload: { sessionId: sessionIdRef.current },
    }));
    termRef.current?.write('\r\n[Disconnected]\r\n');
    sessionIdRef.current = null;
    setSessionId(null);
  };

  const isConnected = !!sessionId;

  return (
    <div style={fullscreen
      ? { position: 'fixed', inset: 0, zIndex: 9990, display: 'flex', flexDirection: 'column' }
      : {}
    }>
      <div style={{
        background: '#1e1e2e',
        borderRadius: fullscreen ? 0 : '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: fullscreen ? '100vh' : undefined,
        boxShadow: fullscreen ? 'none' : '0 4px 24px rgba(0,0,0,0.18)',
      }}>
        {/* Traffic-light title bar */}
        <div style={{
          background: '#2d2d3f',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          userSelect: 'none',
          flexShrink: 0,
        }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ffbd2e', display: 'inline-block' }} />
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
          <span style={{ flex: 1, textAlign: 'center', fontSize: 12, color: '#888', fontFamily: 'monospace' }}>
            {hostname} — bash
            {isConnected
              ? <span style={{ color: '#28c840', marginLeft: 8 }}>● connected</span>
              : wsConnected
                ? <span style={{ color: '#888', marginLeft: 8 }}>○ disconnected</span>
                : <span style={{ color: '#ff5f57', marginLeft: 8 }}>● agent offline</span>
            }
          </span>

          {/* Action buttons */}
          {isConnected && (
            <button
              onClick={handleDisconnect}
              style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 11, padding: '0 6px' }}
              title="Disconnect PTY session"
            >
              disconnect
            </button>
          )}
          {!isConnected && wsConnected && !connecting && (
            <button
              onClick={handleConnect}
              style={{ background: '#28c840', border: 'none', color: '#0d0d14', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 4 }}
            >
              Connect Terminal
            </button>
          )}
          {connecting && (
            <span style={{ fontSize: 11, color: '#ffbd2e' }}>
              <span className="spinner-border spinner-border-sm me-1" style={{ width: '0.6rem', height: '0.6rem' }} />
              connecting...
            </span>
          )}
          <button
            onClick={onFullscreenToggle}
            style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            <i className={`bi bi-${fullscreen ? 'fullscreen-exit' : 'fullscreen'}`} />
          </button>
        </div>

        {/* xterm.js container */}
        <div
          ref={containerRef}
          style={{
            flex: 1,
            minHeight: fullscreen ? undefined : 440,
            maxHeight: fullscreen ? undefined : 540,
            background: '#0d0d14',
            padding: '4px 0',
          }}
          onClick={() => termRef.current?.focus()}
        />

        {/* Status bar */}
        {!isConnected && (
          <div style={{
            background: '#0d0d14',
            borderTop: '1px solid #2d2d3f',
            padding: '8px 16px',
            fontSize: 12,
            color: '#4a4a6a',
            fontFamily: 'monospace',
          }}>
            {!wsConnected
              ? '⚠  Agent is offline — ensure the CloudAyn agent is running on this server'
              : connecting
                ? 'Waiting for agent to start bash process...'
                : 'Click "Connect Terminal" to open an interactive bash session'
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default PtyTerminal;
